// @flow

import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import { Cookies, withCookies } from 'react-cookie';

import type {
  AverageChartComparables,
  BranchLevelGemini,
  HistoryKPIs,
  KPIs,
  ProxyStoreSales,
  ShopHistory,
  ShoppingCenterGemini,
} from '../../../../../types/geminiType';
import {
  erv,
  glaWithoutSst,
  glaWithSst,
  invAbt,
  invAdm,
  invAnn,
  invCtr,
  invFra,
  invLsw,
  invMco,
  invMgr,
  invMwo,
  invOin,
  invPal,
  invPli,
  invRet,
  invRie,
  invSbr,
  invSch,
  kpiSelectorMapping,
  occupancyCost,
  ocr,
  revPot,
  salesR12m,
  salesR12mWithoutSst,
  spotAbt,
  spotAdm,
  spotAnn,
  spotCtr,
  spotFra,
  spotLsw,
  spotMco,
  spotMgr,
  spotMwo,
  spotOin,
  spotPal,
  spotPli,
  spotRet,
  spotRie,
  spotSbr,
  spotSch,
} from '../geminiConst';
import { currencyBySurfaceConvertor, surfaceConvertor } from '../../../../../utils/currencyConvertorUtils';
import { roundPercentage } from '../../../../../utils/formatUtils';
import storeApi from '../../../../../network/api/storesApi';
import type { BranchLevel } from '../../../../../types/apolloType';
import CurrencySurfaceSwitcher from '../../../../common/CurrencySurfaceSwitcher';
import type { SelectConvertorMap } from '../../../../../types/commonTypes';
import ChartKpiSelector from './ChartKpiSelector';
import CloseableComponent from '../../../../common/CloseableComponent';
import { mappingKpiName } from '../../../../../const';
import ChartFilterMenu from './ChartFilterMenu';

type Props = {
  shopHistory: ShopHistory,
  proxyStores: ProxyStoreSales[],
  shoppingCenterData: ShoppingCenterGemini,
  branchKpiAvg: BranchLevelGemini[],
  averageComparables: AverageChartComparables,
  isCurrencyBySurface: boolean,
  currenciesList: SelectConvertorMap,
  cookies: Cookies,
};

type State = {
  selectedKpi: string,
  selectedSliderIndex: number,
  selectedPeriod: number,
  subKpiSelected: {
    ocr: string,
    salesR12m: string,
    glaWithSst: string,
    erv: string,
    spotMgr: string,
    revPot: string,
    invSbr: string,
    occupancyCost: string,
  },
  multipleSelectSelected: {
    ocr: string[],
    salesR12m: string[],
    salesR12mWithoutSst: string[],
    glaWithSst: string[],
    glaWithoutSst: string[],
    erv: string[],
    spotMgr: string[],
    revPot: string[],
    invSbr: string[],
    occupancyCost: string[],
  },
  proxySelected: boolean,
  proxySameBLSelected: boolean,
  showMgrKpi: boolean,
  branchLevelList: BranchLevel[],
  showFilterMenu: boolean,
};

const isSurface = [glaWithSst, glaWithoutSst];
const isPourcentage = [ocr];

const notCurrencyBySurface = [ocr, glaWithSst, glaWithoutSst];

class StoreDetails extends React.Component<Props, State> {
  state = {
    selectedKpi: ocr,
    selectedSliderIndex: 0,
    selectedPeriod: 0,
    subKpiSelected: {
      ocr,
      salesR12m,
      glaWithSst,
      glaWithoutSst,
      erv,
      revPot,
      invSbr,
      spotMgr,
      occupancyCost,
    },
    multipleSelectSelected:
      {
        spotMgr: [
          spotMgr,
          spotSch,
          spotSbr,
          spotRet,
          spotMco,
          spotMwo,
          spotLsw,
          spotRie,
          spotPal,
          spotFra,
          spotAbt,
          spotPli,
          spotAdm,
          spotCtr,
          spotOin,
          spotAnn,
        ],
        occupancyCost: [
          invMgr,
          invSch,
          invRet,
          invMco,
          invMwo,
          invLsw,
          invRie,
          invPal,
          invFra,
          invAbt,
          invPli,
          invAdm,
          invCtr,
          invOin,
          invAnn,
        ],
      },
    proxySelected: false,
    proxySameBLSelected: true,
    showMgrKpi: false,
    branchLevelList: [],
    showFilterMenu: false,
  };

  mgrKpiSelectorButton: ?HTMLButtonElement;
  showFilterMenuButton: ?HTMLButtonElement;

  componentDidMount(): void {
    storeApi
      .fetchAllBranchLevel()
      .then(response => response.json())
      .then((responseJson: BranchLevel[]) => {
        this.setState({ branchLevelList: responseJson });
      });
  }

  computeKpi = (kpi: KPIs) => {
    if (!kpi) {
      return null;
    }
    // Pas de transformation pour les pourcentages et les currencyBySurface
    // quand on passe en CurrencyBySurface
    let divider = this.props.isCurrencyBySurface
      && !notCurrencyBySurface.includes(this.state.selectedKpi)
      ? kpi.glaDen : 1;

    let sum = 0;

    let subKpi = this.state.subKpiSelected[this.state.selectedKpi];
    if (this.state.selectedKpi === salesR12mWithoutSst) {
      divider = this.props.isCurrencyBySurface ? kpi.glaWithoutSst : 1;
      subKpi = salesR12m;
    }
    if (subKpi) {
      const kpiValue = kpi[subKpi];
      if (kpiValue) {
        sum += kpiValue;
      }
    }

    const multipleSelect = this.state.multipleSelectSelected[this.state.selectedKpi];
    if (multipleSelect) {
      multipleSelect.forEach((kpiSelect) => {
        const kpiValue = kpi[kpiSelect];
        if (kpiValue) {
          sum += kpiValue;
        }
      });
    }

    if (notCurrencyBySurface.includes(this.state.selectedKpi)) {
      return sum;
    }

    return sum / divider;
  };

  computeAverage = () => {
    const { selectedKpi } = this.state;
    const { averageComparables, isCurrencyBySurface } = this.props;
    if (averageComparables) {
      let divider = 1;
      if (isCurrencyBySurface) {
        if (!notCurrencyBySurface.includes(selectedKpi)) {
          divider = averageComparables[selectedKpi].glaWithSst;
        }
        if (selectedKpi === salesR12mWithoutSst) {
          divider = averageComparables[selectedKpi].glaWithoutSst;
        }
      }
      const sum = averageComparables[selectedKpi] ? averageComparables[selectedKpi].value : 0;
      return sum / divider;
    }
    return null;
  };

  getHistoryDatasets = () => {
    if (this.state.selectedKpi === salesR12m && this.state.proxySelected) {
      return this.getProxySalesDatasets();
    }
    return [
      {
        backgroundColor: 'rgba(0, 0, 0, 0.0)',
        pointHitRadius: 20,
        borderColor: '#ffffff',
        label: I18n.t('GEMINI.GRAPH.AVERAGE'),
        data: this.props.shoppingCenterData.avg
          .sort((a, b) => a.year - b.year)
          .map(history => this.computeKpi(history)),
      },
      {
        backgroundColor: 'rgba(0, 0, 0, 0.0)',
        borderColor: '#ff00cf',
        pointHitRadius: 20,
        label: this.props.shopHistory.storeName,
        data: this.props.shopHistory.history
          .sort((a, b) => a.year - b.year)
          .map(history => this.computeKpi(history)),
      },
    ];
  }

  getProxySalesDatasets = () => {
    const storeDataset = {
      backgroundColor: 'rgba(0, 0, 0, 0.0)',
      borderColor: '#ff00cf',
      pointHitRadius: 20,
      label: this.props.shopHistory.storeName,
      data: this.props.shopHistory.history
        .sort((a, b) => a.year - b.year)
        .map(history => this.computeKpi(history)),
    };

    if (this.props.proxyStores == null) {
      return [storeDataset];
    }

    const years = this.props.shopHistory.history.map(history => history.year);

    const proxyDatasets = this.props.proxyStores
      .filter((proxyStore: ProxyStoreSales) => (
        !(
          this.state.proxySameBLSelected
          && proxyStore.branchLevelId !== this.props.shopHistory.bl1Id
        )
      )).map((proxyStore: ProxyStoreSales) => (
        {
          backgroundColor: 'rgba(0, 0, 0, 0.0)',
          pointHitRadius: 20,
          borderColor: '#a0cbe8',
          label: proxyStore.storeName,
          data: years.map((year) => {
            const kpiYear: HistoryKPIs = proxyStore.kpisByYear[year.toString()];
            return this.computeKpi(kpiYear);
          }),
        }
      ));

    const averageProxyStoreDataset = {
      backgroundColor: 'rgba(0, 0, 0, 0.0)',
      pointHitRadius: 20,
      borderColor: '#ffffff',
      label: I18n.t('GEMINI.GRAPH.AVERAGE'),
      data: years.map((year, index) => {
        let sum = 0;
        let count = 0;
        proxyDatasets.forEach((dataset) => {
          const data = dataset.data[index];
          if (data != null && isFinite(data)) {
            sum += data;
            count += 1;
          }
        });
        return (count !== 0) ? sum / count : null;
      }),
    };

    proxyDatasets.push(storeDataset);
    proxyDatasets.push(averageProxyStoreDataset);
    return proxyDatasets;
  }

  setSelectedKpi = (kpi: string) => {
    this.setState({ selectedKpi: kpi });
  }

  setSelectedSubKpi = (kpi: string, subKpi: string) => {
    this.state.subKpiSelected[kpi] = subKpi;
    this.forceUpdate();
  }

  setSelectMultipleKpi = (multipleKpis: string[]) => {
    this.state.multipleSelectSelected[this.state.selectedKpi] = multipleKpis;
    this.forceUpdate();
  }

  toggleShowMgrKpi = () => {
    this.setState({ showMgrKpi: !this.state.showMgrKpi });
  }

  toggleShowFilterMenu = () => {
    this.setState({ showFilterMenu: !this.state.showFilterMenu });
  }

  formatValueTicks = (value: number) => {
    const { selectedKpi } = this.state;
    if (isPourcentage.includes(selectedKpi)) {
      return roundPercentage(parseFloat(value));
    }
    if (isSurface.includes(selectedKpi)) {
      return surfaceConvertor(value, this.props.cookies);
    }
    return currencyBySurfaceConvertor(
      value, this.props.cookies, this.props.currenciesList,
      this.props.isCurrencyBySurface, 1, true, '',
    );
  }

  render() {
    const {
      branchLevelList, subKpiSelected, multipleSelectSelected, selectedSliderIndex, showMgrKpi,
      proxySelected, proxySameBLSelected, selectedPeriod, selectedKpi, showFilterMenu,
    } = this.state;

    const barSelectedPeriod = selectedSliderIndex === 0 ? selectedPeriod : 0;
    const barSelectedKpi = this.props.shopHistory.history
      .sort((a, b) => b.year - a.year)[barSelectedPeriod];
    const barScAvg = this.props.shoppingCenterData.avg
      .sort((a, b) => b.year - a.year)[barSelectedPeriod];
    const barSelectedbl1 = this.props.branchKpiAvg
      .filter(branch => branch.blId === this.props.shopHistory.bl1Id)
      .sort((a, b) => b.year - a.year)[barSelectedPeriod];
    const barSelectedbl2 = this.props.branchKpiAvg
      .filter(branch => branch.blId === this.props.shopHistory.bl2Id)
      .sort((a, b) => b.year - a.year)[barSelectedPeriod];

    const brancheLevel1 = branchLevelList
      && branchLevelList.find(bl1 => bl1.id === this.props.shopHistory.bl1Id);
    const titleBl1 = brancheLevel1
      ? I18n.t('GEMINI.GRAPH.SAME_BL1', { blname: brancheLevel1.label })
      : I18n.t('GEMINI.GRAPH.SAME_BL1_undefined');
    const titleBl2 = brancheLevel1
      ? I18n.t('GEMINI.GRAPH.SAME_BL2', { blname: brancheLevel1.listBl.find(bl2 => bl2.id === this.props.shopHistory.bl2Id).label })
      : I18n.t('GEMINI.GRAPH.SAME_BL2_undefined');

    return (
      <div className="gemini-chart">
        <div className="chart-filters">
          <div className="filter-menu-container">
            <button
              className="filter-menu-button"
              onClick={this.toggleShowFilterMenu}
              ref={(ref: HTMLButtonElement) => {
                if (ref && !this.showFilterMenuButton) {
                  this.showFilterMenuButton = ref;
                }
              }}
            >
              <i className="fa fa-bars" />
              <Translate value={mappingKpiName[selectedKpi]} />
            </button>
            <ChartFilterMenu
              selectedKpi={selectedKpi}
              subKpiSelected={subKpiSelected}
              setSelectedKpi={this.setSelectedKpi}
              setSelectedSubKpi={this.setSelectedSubKpi}
            />
            {
              showFilterMenu && (
                <CloseableComponent
                  isOpened={showFilterMenu}
                  toggle={this.toggleShowFilterMenu}
                  buttonRef={this.showFilterMenuButton}
                >
                  <ChartFilterMenu
                    selectedKpi={selectedKpi}
                    subKpiSelected={subKpiSelected}
                    setSelectedKpi={this.setSelectedKpi}
                    setSelectedSubKpi={this.setSelectedSubKpi}
                  />
                </CloseableComponent>
              )
            }
          </div>
          <div className="filter">
            <div className="filter-header"><Translate value="SIDE_MENU.CURRENCY_LOWER" /></div>
            <CurrencySurfaceSwitcher displaySelector />
          </div>
        </div>
        <div className="gemini-graphs-container">
          {
            (kpiSelectorMapping[selectedKpi].multipleSelect.length !== 0)
            && (
              <div className="kpi-filters">
                <button
                  className="select-sub-kpi-filters"
                  onClick={this.toggleShowMgrKpi}
                  ref={(ref: HTMLButtonElement) => {
                    if (ref && !this.mgrKpiSelectorButton) {
                      this.mgrKpiSelectorButton = ref;
                    }
                  }}
                >
                  <Translate
                    value="GEMINI.SELECT_FILTERS"
                  />
                </button>
                <Translate
                  value="GEMINI.NB_KPI_SELECTED"
                  className="little-italic"
                  number={multipleSelectSelected[selectedKpi].length}
                />
                {
                  showMgrKpi
                  && (
                    <CloseableComponent
                      isOpened={showMgrKpi}
                      toggle={this.toggleShowMgrKpi}
                      buttonRef={this.mgrKpiSelectorButton}
                    >
                      <ChartKpiSelector
                        closeModale={this.toggleShowMgrKpi}
                        selectedKpi={selectedKpi}
                        selectedFilters={multipleSelectSelected[selectedKpi]}
                        setSelectedFilter={this.setSelectMultipleKpi}
                      />
                    </CloseableComponent>
                  )
                }
              </div>
            )
          }
          <div className="gemini-graph-container">
            <div className="graph-container">
              <span className="graph-title">{I18n.t('GEMINI.GRAPH.AGGREGATE')}</span>
              <div className="graph">
                <Bar
                  legend={null}
                  options={{
                    scales: {
                      yAxes: [{
                        ticks: {
                          callback: value => this.formatValueTicks(value),
                        },
                        gridLines: {
                          color: 'rgba(206,206,206,0.58)',
                        },
                      }],
                      xAxes: [{
                        gridLines: {
                          color: 'rgba(206,206,206,0.58)',
                        },
                      }],
                    },
                    maintainAspectRatio: false,
                    tooltips: {
                      callbacks: {
                        label: tooltipItem => this.formatValueTicks(parseFloat(tooltipItem.value)),
                      },
                    },
                    data: {
                      labels: {
                        color: 'rgba(206,206,206,0.58)',
                      },
                    },
                  }}
                  data={{
                    labels: [
                      I18n.t('GEMINI.GRAPH.ITSELF'),
                      I18n.t('GEMINI.GRAPH.SAME_SC'),
                      titleBl1,
                      titleBl2,
                      I18n.t('GEMINI.GRAPH.AVERAGE_COMPARABLES'),
                    ],
                    datasets: [
                      {
                        backgroundColor: '#ffffff',
                        data: [
                          this.computeKpi(barSelectedKpi),
                          this.computeKpi(barScAvg),
                          this.computeKpi(barSelectedbl1),
                          this.computeKpi(barSelectedbl2),
                          this.computeAverage(),
                        ],
                      },
                    ],
                  }}
                />
              </div>
              <div className="period-container">
                <button
                  className={`legend-elt ${selectedPeriod === 0 ? 'selected' : ''}`}
                  onClick={() => this.setState({ selectedPeriod: 0 })}
                >
                  <Translate value="GEMINI.THIS_MONTH" />
                </button>
                <button
                  className={`legend-elt ${selectedPeriod === 1 ? 'selected' : ''}`}
                  onClick={() => this.setState({ selectedPeriod: 1 })}
                >
                  <Translate value="GEMINI.LAST_YEAR" />
                </button>
              </div>
            </div>
            <div className="graph-container">
              <span className="graph-title">{I18n.t('GEMINI.GRAPH.HISTORY')}</span>
              <div className="graph-legend-container">
                {
                  selectedKpi === salesR12m &&
                  <div className="sales-history-legend-container">
                    <div className="period-container">
                      <div className="legend-elt">
                        <div className="legend-color store" />
                        {this.props.shopHistory.storeName}
                      </div>
                      <div className="legend-elt">
                        <div className="legend-color average" />
                        {I18n.t('GEMINI.GRAPH.AVERAGE')}
                      </div>
                      {
                        proxySelected
                        && (
                          <div className="legend-elt">
                            <div className="legend-color proxy-stores" />
                            {I18n.t('GEMINI.GRAPH.PROXY_STORES')}
                          </div>
                        )
                      }
                    </div>
                  </div>
                }
                <div className="graph">
                  <Line
                    legend={null}
                    data={{
                      labels: this.props.shopHistory.history
                        .sort((a, b) => a.year - b.year)
                        .map(history => `${history.year}/${history.month}`),
                      datasets: this.getHistoryDatasets(),
                    }}
                    options={{
                      scales: {
                        yAxes: [{
                          ticks: {
                            callback: value => this.formatValueTicks(value),
                          },
                          gridLines: {
                            color: 'rgba(206,206,206,0.58)',
                          },
                        }],
                        xAxes: [{
                          gridLines: {
                            color: 'rgba(206,206,206,0.58)',
                          },
                        }],
                      },
                      maintainAspectRatio: false,
                      tooltips: {
                        callbacks: {
                          label: (tooltipItem, data) => {
                            const storeName = data.datasets[tooltipItem.datasetIndex].label || '';
                            const value = this.formatValueTicks(tooltipItem.value);
                            return `${storeName} : ${value}`;
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
              {selectedKpi === salesR12m ? (
                <>
                  {
                    proxySelected
                    && (
                      <div className="filter">
                        <div className="filter-header">
                          {I18n.t('GEMINI.GRAPH.BL')}
                        </div>
                        <div className="selector-buttons-row">
                          <button
                            className={`selector-buttons ${!proxySameBLSelected ? 'selected' : ''}`}
                            onClick={() => this.setState({ proxySameBLSelected: false })}
                          >
                            <Translate value="GEMINI.ALL_BL" />
                          </button>
                          <button
                            className={`selector-buttons ${proxySameBLSelected ? 'selected' : ''}`}
                            onClick={() => this.setState({ proxySameBLSelected: true })}
                          >
                            <Translate value="GEMINI.SAME_BL" />
                          </button>
                        </div>
                      </div>
                    )
                  }
                  <div className="period-container">
                    <button
                      className={`legend-elt ${!proxySelected ? 'selected' : ''}`}
                      onClick={() => this.setState({ proxySelected: false })}
                    >
                      <Translate value="GEMINI.ALL_SALES" />
                    </button>
                    <button
                      className={`legend-elt ${proxySelected ? 'selected' : ''}`}
                      onClick={() => this.setState({ proxySelected: true })}
                    >
                      <Translate value="GEMINI.PROXY_SALES" />
                    </button>
                  </div>
                </>
              ) : (
                <div className="period-container">
                  <div className="legend-elt">
                    <div className="legend-color store" />
                    {this.props.shopHistory.storeName}
                  </div>
                  <div className="legend-elt">
                    <div className="legend-color average" />
                    {I18n.t('GEMINI.GRAPH.AVERAGE')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withCookies(connect(state => ({
  isCurrencyBySurface: state.store.isCurrencyBySurface,
  currenciesList: state.dynamicKeys.currenciesList,
}))(StoreDetails));
